import MoreVertIcon from '@mui/icons-material/MoreVert';
import { Fade, IconButton, Menu, MenuItem, MenuList, useTheme } from '@mui/material';
import { useAtomValue } from 'jotai';
import { FC, MouseEvent, useCallback, useState } from 'react';
import { useToastMessageState } from '../../../components/ToastMessage/ToastMessageProvider';
import { refreshCaptableOwnershipChanges } from '../../../services/queries/MaggieCompanyQueries';
import { selectedCompanyIdProfileAtom } from '../state/UIStateJ';

export const CartaActionsMenu: FC = () => {
  const { colors } = useTheme();
  const companyId = useAtomValue(selectedCompanyIdProfileAtom);
  const { pushSuccessToast, pushErrorToast } = useToastMessageState();
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const open = Boolean(anchorEl);

  const handleOpen = (event: MouseEvent<HTMLElement>) => {
    event.stopPropagation();
    setAnchorEl(event.currentTarget);
  };

  const handleClose = useCallback(() => {
    setAnchorEl(null);
  }, []);

  const handleRefresh = useCallback(async () => {
    if (companyId < 0) {
      return;
    }
    handleClose();
    setIsRefreshing(true);
    try {
      await refreshCaptableOwnershipChanges(companyId);
      pushSuccessToast({ message: 'Captable ownership change transactions refreshed' });
    } catch (err) {
      console.error('Captable ownership change refresh failed:', err);
      pushErrorToast({ message: 'Failed to refresh captable ownership change transactions' });
    } finally {
      setIsRefreshing(false);
    }
  }, [companyId, handleClose, pushSuccessToast, pushErrorToast]);

  return (
    <>
      <IconButton
        aria-label='Captable actions'
        aria-controls={open ? 'captable-actions-menu' : undefined}
        aria-haspopup='true'
        aria-expanded={open ? 'true' : undefined}
        onClick={handleOpen}
        color='secondary'
        size='medium'
      >
        <MoreVertIcon sx={{ color: colors.primary[60] }} />
      </IconButton>
      <Menu
        id='captable-actions-menu'
        anchorEl={anchorEl}
        open={open}
        onClose={handleClose}
        TransitionComponent={Fade}
        elevation={2}
        MenuListProps={{ 'aria-labelledby': 'captable-actions-menu' }}
      >
        <MenuList sx={{ py: 0 }}>
          <MenuItem onClick={handleRefresh} disabled={isRefreshing}>
            {isRefreshing ? 'Refreshing…' : 'Refresh captable based ownership change transactions'}
          </MenuItem>
        </MenuList>
      </Menu>
    </>
  );
};
